import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import {
  getBankReconciliationProposals,
  applyBankReconciliation
} from '../../services/retailOrdersService';
import { PageHeader } from '../ds';

// The reconciliation endpoints are brand-scoped and default to Sand & Sky on
// the backend. The wholesale screens are otherwise cross-brand, but bank-credit
// matching runs per brand DB — brand 10 is the active wholesale brand. (Wire to
// a brand selector later if other brands need it — see follow-ups in the PR.)
const BRAND_ID = 10;

// Unique row identity. A bank credit maps to one invoice, but key on the pair so
// a single bank tx proposed against two orders stays independently selectable.
const rowKey = (p) => `${p.bank_transaction_id}-${p.order_id}`;

// Strip the CIN7- prefix off order ids for display, matching ReadyToInvoice.
const stripOrderId = (id) => String(id || '').replace(/^CIN7-/, '');

const money = (amount, currency) =>
  `${currency || ''} ${Number(amount ?? 0).toFixed(2)}`.trim();

export default function BankReconciliation() {
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getBankReconciliationProposals(BRAND_ID);
      const rows = resp.proposals || [];
      setProposals(rows);
      // Default: every proposed match is checked — the operator confirms the
      // set and unchecks any that look wrong.
      setSelected(new Set(rows.map(rowKey)));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allKeys = useMemo(() => proposals.map(rowKey), [proposals]);
  const allChecked = proposals.length > 0 && selected.size === proposals.length;
  const someChecked = selected.size > 0 && selected.size < proposals.length;

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(allKeys) : new Set());
  };

  const toggleRow = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const apply = async () => {
    const items = proposals
      .filter(p => selected.has(rowKey(p)))
      .map(p => ({
        order_id: p.order_id,
        bank_transaction_id: p.bank_transaction_id,
        bank_source: p.bank_source,
        amount: p.amount,
      }));
    if (items.length === 0) return;
    try {
      setApplying(true);
      setError(null);
      const resp = await applyBankReconciliation(BRAND_ID, items);
      const applied = resp.applied ?? items.length;
      setSuccess(`Reconciled ${applied} payment${applied === 1 ? '' : 's'}.`);
      // Re-fetch so the applied (now-paid) rows drop off the list.
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <PageHeader
        primary="Bank Reconciliation"
        metadata={[{
          label: proposals.length > 0
            ? `${proposals.length} proposed reconciliation${proposals.length === 1 ? '' : 's'} — confirm the ones that are correct.`
            : 'Incoming bank credits matched to open invoices.',
        }]}
        actions={
          <Box display="flex" alignItems="center" gap={1}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={applying}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={applying ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              onClick={apply}
              disabled={applying || selected.size === 0}
            >
              {applying ? 'Applying…' : `Apply ${selected.size} selected`}
            </Button>
          </Box>
        }
      />

      <Alert severity="info" icon={<AccountBalanceIcon fontSize="inherit" />} sx={{ mb: 2 }}>
        Source: incoming bank credits (Wise/Revolut) matched to open invoices by exact amount.
        Confirm before applying — the amount matches but verify the payer is the right customer.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          {proposals.length === 0 ? (
            <Box textAlign="center" py={4}>
              <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary">
                No bank payments to reconcile — all matched.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someChecked}
                        checked={allChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                        title="Select all / none"
                      />
                    </TableCell>
                    <TableCell>Bank received</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>Credit date</TableCell>
                    <TableCell align="center">→</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>QB invoice</TableCell>
                    <TableCell align="right">Order total</TableCell>
                    <TableCell>Order id</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposals.map(p => {
                    const key = rowKey(p);
                    const checked = selected.has(key);
                    return (
                      <TableRow key={key} hover selected={checked}
                        sx={checked ? {} : { opacity: 0.5 }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked} onChange={() => toggleRow(key)} />
                        </TableCell>
                        <TableCell>
                          <strong>{money(p.amount, p.currency)}</strong>
                        </TableCell>
                        <TableCell>{p.bank_says || '—'}</TableCell>
                        <TableCell>{p.credit_date || '—'}</TableCell>
                        <TableCell align="center">
                          <Typography color="text.disabled">→</Typography>
                        </TableCell>
                        <TableCell>{p.customer_name || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {p.qb_invoice_id || '—'}
                        </TableCell>
                        <TableCell align="right">{money(p.order_total, p.currency)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {stripOrderId(p.order_id)}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => toggleRow(key)}
                            color={checked ? 'inherit' : 'primary'}>
                            {checked ? 'Skip' : 'Include'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
